import classNames from 'classnames';
import React from 'react';
import { Modal as BModal } from 'react-bootstrap';
import styled from 'styled-components';

import { modalBottomSvg, modalTopSvg } from '../assets/images';
import { Button } from './Button';
import { Icon } from './Icon';
import { Tag } from './Tag';

export type BackdropType = boolean | 'static' | undefined;

export interface ModalProps {
  exampleBg?: boolean;
  show?: boolean;
  title?: string;
  children?: React.ReactNode;
  submitButtonText?: string;
  cancelButtonText?: string;
  onClose?: () => void;
  onSubmit?: () => void;
  submitButtonDisabled?: boolean;
  cancelButtonDisabled?: boolean;
  type?: 'default' | 'live-class' | 'survey' | 'play2learn-invitation' | string;
  className?: string;
  hideCloseButton?: boolean;
  backdrop?: BackdropType;
}

export const Modal: React.FC<ModalProps> = ({
  type,
  exampleBg,
  children,
  title,
  onSubmit,
  onClose,
  submitButtonText,
  cancelButtonText,
  show,
  className,
  hideCloseButton,
  submitButtonDisabled,
  cancelButtonDisabled,
  backdrop,
}) => {
  const TypeTag = () => {
    if (type === 'live-class') {
      return (
        <div className="oe-tag-wrapper">
          <Tag icon="live" shape="vertical-label" variant="yellow-main">
            Live Class
          </Tag>
        </div>
      );
    }
    return <></>;
  };

  const CancelButton = () => {
    if (!cancelButtonText) {
      return null;
    }
    return (
      <Button
        label={cancelButtonText}
        onClick={onClose}
        variant="subtle"
        className="oejr-btn-cancel"
        onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
        }}
        disabled={cancelButtonDisabled}
      />
    );
  };

  const SubmitButton = () => {
    if (!submitButtonText) {
      return null;
    }
    return (
      <Button
        label={submitButtonText}
        onClick={onSubmit}
        variant="primary"
        disabled={submitButtonDisabled}
        onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
        }}
      />
    );
  };

  const CloseButton = () => {
    if (hideCloseButton) {
      return null;
    }
    return (
      <div className="oejr-modal-close-btn" onClick={onClose}>
        <Icon name="times" />
      </div>
    );
  };

  return (
    <BModal
      show={show}
      onHide={onClose}
      centered
      dialogClassName="oejr-modal-wrapper"
      backdrop={backdrop}
    >
      <div
        className={classNames(
          'oejr-modal',
          exampleBg && `oejr-modal-example-bg`,
          `oejr-modal-${type}`,
          className,
        )}
      >
        <div className="oejr-modal-container-before">
          <img src={modalTopSvg} alt="Modal" />
        </div>
        <div className={classNames('oejr-modal-container oejr-modal-container-live-class')}>
          <TypeTag />
          {!!title && (
            <div className="oejr-modal-header">
              <h2 className="oejr-modal-title">{title}</h2>
            </div>
          )}
          <CloseButton />
          <div className="oejr-modal-body">{children}</div>
          <div className="oejr-modal-footer">
            <StyledButtonsWrapper>
              <div className="d-none d-md-block">
                <CancelButton />
                <SubmitButton />
              </div>
              <div className="d-block d-md-none">
                <SubmitButton />
                <CancelButton />
              </div>
            </StyledButtonsWrapper>
          </div>
        </div>
        <div className="oejr-modal-container-after">
          <img src={modalBottomSvg} alt="Modal" />
        </div>
      </div>
    </BModal>
  );
};

const StyledButtonsWrapper = styled.div`
  text-align: center;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.md}) {
    display: flex;
    flex-direction: column;

    .btn {
      width: 100%;
      margin-bottom: 12px;
    }
  }
`;
